
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import {
  getVAStatistics,
  getExpiringUsers,
  getVAStatus,
  grantVA,
  extendVA,
  revokeVA,
  VAStatistics,
  ExpiringUser,
  VAStatus
} from '@/api/voiceAssistant'
import Pagination from '@/components/Pagination/index.vue'
import UserSearch from '@/components/UserSearch/index.vue'

@Component({
  name: 'VoiceAssistantManagement',
  components: {
    Pagination,
    UserSearch
  }
})
export default class extends Vue {
  private statistics: VAStatistics = {
    totalActiveUsers: 0,
    preOrderUsers: 0,
    regularUsers: 0,
    trialUsers: 0,
    expiringIn30Days: 0,
    expiringIn90Days: 0
  }

  private expiringUsers: ExpiringUser[] = []
  private listLoading = false
  private daysFilter = 30
  private total = 0
  private listQuery = {
    page: 1,
    limit: 20
  }

  private dialogStatusVisible = false
  private dialogExtendVisible = false
  private dialogGrantVisible = false

  private statusData: VAStatus = {
    hasAccess: false,
    entitlements: []
  }

  private extendData: any = {
    userId: '',
    currentExpiry: '',
    extensionMonths: 6,
    reason: ''
  }

  private extendRules = {
    extensionMonths: [{ required: true, message: 'Please select extension period', trigger: 'change' }],
    reason: [{ required: true, message: 'Please provide a reason', trigger: 'blur' }]
  }

  private grantData: any = {
    userId: '',
    durationMonths: 12,
    deviceSerialNumber: '',
    reason: ''
  }

  private grantRules = {
    userId: [{ required: true, message: 'Please enter user ID', trigger: 'blur' }],
    durationMonths: [{ required: true, message: 'Please select duration', trigger: 'change' }],
    reason: [{ required: true, message: 'Please provide a reason', trigger: 'blur' }]
  }

  created() {
    this.loadStatistics()
    this.loadExpiringUsers()
  }

  private async loadStatistics() {
    try {
      const response: any = await getVAStatistics()
      const res = response.data || response
      if (res.success) {
        this.statistics = res.data
      }
    } catch (error) {
      console.error('Failed to load statistics', error)
    }
  }

  private async loadExpiringUsers() {
    this.listLoading = true
    try {
      const response: any = await getExpiringUsers(this.daysFilter)
      const res = response.data || response
      if (res.success) {
        this.expiringUsers = res.data
        this.total = res.count || this.expiringUsers.length
      }
    } catch (error) {
      this.$message.error('Failed to load expiring users')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private async handleViewStatus(row: ExpiringUser) {
    try {
      const response: any = await getVAStatus(row.userId)
      const res = response.data || response
      if (res.success) {
        this.statusData = res.data
        this.statusData.userId = row.userId
        this.dialogStatusVisible = true
      }
    } catch (error) {
      this.$message.error('Failed to load VA status')
      console.error(error)
    }
  }

  private handleExtend(row: ExpiringUser) {
    this.extendData = {
      userId: row.userId,
      currentExpiry: this.formatDate(row.entitlement.endDate),
      currentExpiryDate: new Date(row.entitlement.endDate),
      extensionMonths: 6,
      reason: ''
    }
    this.dialogExtendVisible = true
    this.$nextTick(() => {
      (this.$refs.extendForm as ElForm).clearValidate()
    })
  }

  private async confirmExtend() {
    (this.$refs.extendForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await extendVA(
            this.extendData.userId,
            this.extendData.extensionMonths,
            this.extendData.reason
          )
          this.$message.success('Voice Assistant extended successfully')
          this.dialogExtendVisible = false
          await this.loadExpiringUsers()
          await this.loadStatistics()
        } catch (error: any) {
          this.$message.error(error.response?.data?.error || 'Extension failed')
          console.error(error)
        }
      }
    })
  }

  private handleRevoke(row: ExpiringUser) {
    this.$confirm(
      'Are you sure you want to revoke Voice Assistant access for this user? This action cannot be undone.',
      'Warning',
      {
        confirmButtonText: 'Yes, Revoke',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    ).then(async() => {
      try {
        await revokeVA(row.userId, 'Revoked by admin')
        this.$message.success('Voice Assistant revoked successfully')
        await this.loadExpiringUsers()
        await this.loadStatistics()
      } catch (error: any) {
        this.$message.error(error.response?.data?.error || 'Revoke failed')
        console.error(error)
      }
    }).catch(() => {
      this.$message.info('Revoke cancelled')
    })
  }

  private handleGrantNew() {
    this.grantData = {
      userId: '',
      durationMonths: 12,
      deviceSerialNumber: '',
      reason: ''
    }
    this.dialogGrantVisible = true
    this.$nextTick(() => {
      (this.$refs.grantForm as ElForm).clearValidate()
    })
  }

  private handleUserSelected(userId: string, user: any) {
    // User selected from search component
    if (user) {
      console.log('Selected user:', user.firstName, user.lastName, user.email)
    }
  }

  private confirmGrant() {
    (this.$refs.grantForm as ElForm).validate(async(valid) => {
      if (valid) {
        try {
          await grantVA(
            this.grantData.userId,
            this.grantData.durationMonths,
            this.grantData.reason,
            undefined, // adminId - will be inferred from session
            this.grantData.deviceSerialNumber || undefined
          )
          this.$message.success('Voice Assistant rights granted successfully')
          this.dialogGrantVisible = false
          await this.loadExpiringUsers()
          await this.loadStatistics()
        } catch (error: any) {
          const errorMsg = error.response?.data?.error || 'Grant failed'
          this.$message.error(errorMsg)
          console.error(error)
        }
      }
    })
  }

  private exportExcel() {
    import('@/vendor/Export2Excel').then((excel) => {
      const tHeader = ['User ID', 'Expiry Date', 'Days Remaining', 'Source', 'Source Reference']
      const filterVal = ['userId', 'expiryDate', 'daysRemaining', 'sourceType', 'sourceReference']
      const data = this.expiringUsers.map((row) => ({
        userId: row.userId,
        expiryDate: this.formatDate(row.entitlement.endDate),
        daysRemaining: row.daysRemaining,
        sourceType: row.entitlement.sourceType,
        sourceReference: row.entitlement.sourceReference || 'N/A'
      }))
      const formattedData = data.map((v: any) =>
        filterVal.map((j) => v[j])
      )
      excel.export_json_to_excel({
        header: tHeader,
        data: formattedData,
        filename: `VA_Expiring_Users_${this.daysFilter}days_${new Date().toISOString().split('T')[0]}`
      })
    })
  }

  private calculateNewExpiry(): string {
    if (!this.extendData.currentExpiryDate || !this.extendData.extensionMonths) {
      return 'N/A'
    }
    const newDate = new Date(this.extendData.currentExpiryDate)
    newDate.setMonth(newDate.getMonth() + this.extendData.extensionMonths)
    return newDate.toLocaleDateString()
  }

  private getDaysTag(days: number): string {
    if (days < 0) return 'danger'
    if (days < 7) return 'danger'
    if (days < 30) return 'warning'
    return 'info'
  }

  private getSourceTag(sourceType: string): string {
    const typeMap: { [key: string]: string } = {
      'device_connection': 'success',
      'zoho_purchase': 'warning',
      'admin_grant': 'danger'
    }
    return typeMap[sourceType] || 'info'
  }

  private formatDate(date: Date | string | undefined | null): string {
    if (!date) return 'N/A'
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    const hours = d.getHours().toString().padStart(2, '0')
    const minutes = d.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }
}
